import { useEffect, useState } from 'react';
import type { DatetimeField } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { bt } from '../../i18n/schema-text';

/**
 * The moment picker (feature 0041 V5) — the control behind `type: 'datetime'`.
 *
 * ## The conversion is the whole point
 *
 * A native `<input type="datetime-local">` speaks the seller's WALL CLOCK (`2026-12-31T23:59`), with no
 * timezone in it at all. Storing that string would mean a deadline that ends at 23:59 in Casablanca, at
 * 23:59 in Paris and at 23:59 in Montreal — twenty-four different endings for one sale.
 *
 * So `datetime` mode stores an ABSOLUTE instant (ISO-8601 UTC) and converts on both sides: the seller
 * types their own 23:59, every buyer on earth counts down to the same second, and the field shows them
 * back the same 23:59 they typed. `date` and `time` modes have no instant to anchor and store the plain
 * `YYYY-MM-DD` / `HH:MM` the input gives.
 *
 * ## Why the value can be cleared
 *
 * An empty deadline is a MEANINGFUL state — "Show from" empty means "right away", and a countdown whose
 * end date is not picked yet is the normal state of a section the seller has just added. Emptying the
 * input therefore writes `''`, it does not silently keep the previous instant (the "pick without an
 * undo" failure of the app's CTA product picker, 24/09/2026).
 */
export function DatetimeControl({
  field,
  value,
  onChange,
}: {
  field: DatetimeField;
  value: string | undefined;
  onChange: (next: string) => void;
}) {
  const mode = field.mode ?? 'datetime';
  const [text, setText] = useState(() => toInput(value, mode));
  useEffect(() => setText(toInput(value, mode)), [value, mode]);

  const inputType = mode === 'date' ? 'date' : mode === 'time' ? 'time' : 'datetime-local';

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} layout="row">
      <div className="bld-number">
        <input
          id={field.id}
          className="bld-input"
          type={inputType}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            onChange(fromInput(e.target.value, mode));
          }}
        />
        {text !== '' ? (
          <button
            type="button"
            className="bld-datetime__clear"
            aria-label={bt('control.datetime.clear', 'Clear')}
            title={bt('control.datetime.clear', 'Clear')}
            onClick={() => {
              setText('');
              onChange('');
            }}
          >
            ×
          </button>
        ) : null}
      </div>
    </FieldRow>
  );
}

/** Two digits, the shape every part of an `<input type="datetime-local">` value takes. */
const p2 = (n: number): string => (n > 9 ? String(n) : `0${n}`);

/** Stored value → what the input shows (the seller's own wall clock for an absolute instant). */
export function toInput(stored: string | undefined, mode: 'datetime' | 'date' | 'time'): string {
  const raw = typeof stored === 'string' ? stored.trim() : '';
  if (raw === '') return '';
  if (mode !== 'datetime') return raw;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(
    d.getMinutes(),
  )}`;
}

/** What the input gave → what gets stored (an absolute instant for `datetime`). */
export function fromInput(raw: string, mode: 'datetime' | 'date' | 'time'): string {
  if (raw === '') return '';
  if (mode !== 'datetime') return raw;
  // `new Date('2026-12-31T23:59')` — no zone — is read as LOCAL time by every engine, which is exactly
  // the seller's own clock. `toISOString` then hands us the instant.
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : '';
}
